import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Drawer, Loader, MultiSelect, NumberInput, Select, Switch, TextInput, UnstyledButton } from '@mantine/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashCan, faCopy } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import { DragHandle } from '../../../../../components/DragHandle/DragHandle';
import type { PrinterView } from '../../../../../services/printers/printers.service';
import type { ProductView } from '../../../../../services/products/products.service';
import type { VariantComboGroupView } from '../../../../../services/variants-combos/variants-combos.service';
import { ProductPickerModal } from '../product-picker-modal/ProductPickerModal';
import { useOptionsBuilderDraft } from './useOptionsBuilderDraft';
import {
  type ComboMode,
  type UiGroup,
  type UiItem,
  genRule,
  quotaNote,
  firstNPhrase,
  groupErrors,
  mapGroup,
  mapItem,
  pickLimits,
  removeItemNode,
  reorderGroupsInParent,
  reorderItemsInGroup,
  resolveInheritedItemDefaults,
  setGroupMode,
  setGroupNMax,
  setGroupType,
  toggleDefaultIn,
} from './options-builder.utils';
import classes from './options-builder-drawer.module.scss';

type Props = {
  opened: boolean;
  productName: string;
  productPrice: number;
  currentProductId: number | null;
  productKds: boolean;
  productPrinterIds: number[];
  groups: VariantComboGroupView[];
  activePrinters: PrinterView[];
  allProducts: ProductView[];
  comboItemsByProductId?: Record<number, { id: number; name: string; groupName: string }[]>;
  isLoading: boolean;
  isSaving: boolean;
  isCopyingGroup: boolean;
  onClose: () => void;
  onSave: (uiGroups: UiGroup[]) => void | Promise<void>;
  onCopyGroupToProducts: (group: UiGroup, targetProductIds: number[], targetItemIds: number[]) => void | Promise<void>;
};

const ILS = '₪';
const fmt = (n: number): string => `${ILS}${Number.isInteger(n) ? n : n.toFixed(1)}`;
const normalizeTenths = (value: number): number => Math.round(Math.max(0, value) * 10) / 10;

// Price field: type freely while focused, pad to one decimal (x.y) on blur.
const PriceInput: React.FC<{ value: number; width: number; onChange: (v: string | number) => void; dataTestId?: string }> = ({ value, width, onChange, dataTestId }) => {
  const [focused, setFocused] = useState(false);
  return (
    <NumberInput
      w={width}
      hideControls
      min={0}
      max={9999}
      step={0.1}
      decimalScale={1}
      fixedDecimalScale={!focused}
      value={value}
      onChange={onChange}
      data-testid={dataTestId}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

// Architectural summary: Presentational right-side drawer for editing a product's variants/combos
const OptionsBuilderDrawerComponent: React.FC<Props> = ({
  opened,
  productName,
  productPrice,
  currentProductId,
  productKds,
  productPrinterIds,
  groups,
  activePrinters,
  allProducts,
  comboItemsByProductId,
  isLoading,
  isSaving,
  isCopyingGroup,
  onClose,
  onSave,
  onCopyGroupToProducts,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [copySourceGroup, setCopySourceGroup] = useState<UiGroup | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  useEffect(() => { setReorderMode(false); }, [opened]);
  const itemDefaults = useMemo(
    () => resolveInheritedItemDefaults(productKds, productPrinterIds, activePrinters.map((p) => p.id)),
    [productKds, productPrinterIds, activePrinters],
  );
  const {
    draft,
    setDraft,
    editing,
    toggleEdit,
    showErrors,
    errors,
    addItem,
    addChild,
    delGroup,
    addGroup,
    revealErrors,
  } = useOptionsBuilderDraft({ opened, isLoading, groups, itemDefaults });

  const handleAddGroup = () => {
    addGroup();
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    });
  };

  const handleSave = () => {
    if (revealErrors()) {
      requestAnimationFrame(() => {
        const el = bodyRef.current?.querySelector(`.${classes.groupInvalid}`) as HTMLElement | null;
        if (el && bodyRef.current) bodyRef.current.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' });
      });
      return;
    }
    void onSave(draft);
  };

  const printerOptions = useMemo(
    () => activePrinters.map((p) => ({ value: String(p.id), label: p.name })),
    [activePrinters],
  );

  const api: GroupApi = {
    setDraft,
    editing,
    showErrors,
    reorderMode,
    toggleEdit,
    addItem,
    addChild,
    delGroup,
    printerOptions,
    onStartCopy: setCopySourceGroup,
  };

  return (
    <>
    <Drawer.Root opened={opened} onClose={onClose} position="right" size={600} padding={0}>
      <Drawer.Overlay />
      <Drawer.Content>
        <div className={classes.content}>
          <div className={classes.head}>
            <div>
              <div className={classes.ttl}>וריאציות וקומבו</div>
              <span className={classes.sub}>
                {productName} · <span className={classes.subPrice}>{fmt(productPrice)}</span>
              </span>
            </div>
            <span className={classes.spacer} />
            <UnstyledButton className={classes.xbtn} data-testid="options-builder-close" onClick={onClose} aria-label="סגור">✕</UnstyledButton>
          </div>

          {!isLoading && draft.length > 0 && (
            <div className={classes.bar}>
              <Button
                variant={reorderMode ? 'confirm' : 'save'}
                size="sm"
                onClick={() => setReorderMode((v) => !v)}
                dataTestId="options-builder-reorder-mode"
              >
                {reorderMode ? 'סיום מיון' : 'מיון'}
              </Button>
              {reorderMode && <span className={classes.barHint}>גרור לפי הידית לשינוי הסדר</span>}
            </div>
          )}

          <div className={classes.body} ref={bodyRef}>
            {isLoading ? (
              <div className={classes.empty}><Loader /></div>
            ) : draft.length === 0 ? (
              <div className={classes.empty}>
                <div className={classes.emptyIco}>＋</div>
                <h3 className={classes.emptyTitle}>אין עדיין וריאציות או קומבו</h3>
                <p className={classes.emptyText}>הוסף קבוצה כדי לאפשר ללקוח לבחור גודל, תוספות או רמת עשייה.</p>
                <UnstyledButton className={classes.addGroup} data-testid="options-builder-add-group" style={{ maxWidth: 280, marginTop: 10 }} onClick={handleAddGroup}>
                  ＋ הוסף קבוצה
                </UnstyledButton>
              </div>
            ) : (
              <>
                <SortableGroupList groups={draft} depth={0} api={api} parentItemId={null} sortable={reorderMode} />
                {!reorderMode && (
                  <UnstyledButton className={classes.addGroup} data-testid="options-builder-add-group" onClick={handleAddGroup}>＋ הוסף קבוצה</UnstyledButton>
                )}
              </>
            )}
          </div>

          <div className={classes.foot}>
            <span className={classes.footMsg}>
              {showErrors && errors.length > 0
                ? `יש ${errors.length} ${errors.length === 1 ? 'קבוצה' : 'קבוצות'} עם שגיאות לתיקון`
                : ''}
            </span>
            <Button variant="cancel" onClick={onClose} dataTestId="options-builder-cancel">ביטול</Button>
            <Button variant="save" disabled={isSaving} onClick={handleSave} dataTestId="options-builder-save">
              {isSaving ? 'שומר…' : 'שמור'}
            </Button>
          </div>
        </div>
      </Drawer.Content>
    </Drawer.Root>

    <ProductPickerModal
      opened={copySourceGroup !== null}
      title={copySourceGroup ? `העתק "${copySourceGroup.name || 'קבוצה'}" ליעדים אחרים` : ''}
      hint="מוצר = קבוצה ראשית · פריט בקומבו = קבוצה מקוננת"
      products={allProducts}
      itemsByProductId={comboItemsByProductId}
      excludeProductId={currentProductId}
      isSubmitting={isCopyingGroup}
      confirmLabel="העתק"
      onClose={() => setCopySourceGroup(null)}
      onConfirm={(targetProductIds, targetItemIds) => {
        if (!copySourceGroup) return;
        void onCopyGroupToProducts(copySourceGroup, targetProductIds, targetItemIds);
        setCopySourceGroup(null);
      }}
    />
    </>
  );
};

type GroupApi = {
  setDraft: React.Dispatch<React.SetStateAction<UiGroup[]>>;
  editing: Set<string>;
  showErrors: boolean;
  reorderMode: boolean;
  toggleEdit: (id: string, force?: boolean) => void;
  addItem: (groupId: string) => void;
  addChild: (itemId: string) => void;
  delGroup: (g: UiGroup) => void;
  onStartCopy: (group: UiGroup) => void;
  printerOptions: { value: string; label: string }[];
};

const Stepper: React.FC<{ value: number; set: (v: number) => void; min?: number; max?: number; dataTestId?: string }> = ({
  value, set, min = 0, max = 99, dataTestId,
}) => (
  <div className={classes.stepper} data-testid={dataTestId}>
    <UnstyledButton className={classes.stepBtn} disabled={value <= min} onClick={() => set(Math.max(min, value - 1))} data-testid={dataTestId ? `${dataTestId}-dec` : undefined}>−</UnstyledButton>
    <NumberInput
      className={classes.stepVal}
      variant="unstyled"
      hideControls
      clampBehavior="strict"
      allowNegative={false}
      allowDecimal={false}
      ta="center"
      w={44}
      min={min}
      max={max}
      value={value}
      onChange={(v) => { const n = Number(v); if (Number.isFinite(n)) set(Math.min(max, Math.max(min, Math.round(n)))); }}
      data-testid={dataTestId ? `${dataTestId}-input` : undefined}
    />
    <UnstyledButton className={classes.stepBtn} disabled={value >= max} onClick={() => set(Math.min(max, value + 1))} data-testid={dataTestId ? `${dataTestId}-inc` : undefined}>＋</UnstyledButton>
  </div>
);

const ReadItem: React.FC<{ g: UiGroup; it: UiItem; dragHandle?: React.ReactNode }> = ({ g, it, dragHandle }) => {
  const { max } = pickLimits(g);
  const isVar = g.type === 'variant';
  const freeMode = g.freeOn && g.type === 'combo' && max > 1;
  let priceEl: React.ReactNode;
  if (freeMode) {
    const within = it.priceWithin > 0 ? fmt(it.priceWithin) : 'חינם';
    priceEl = <span className={`${classes.price} ${classes.priceFree}`}>{within} ← {fmt(it.price)}</span>;
  } else {
    priceEl = it.price > 0
      ? <span className={classes.price}>+{fmt(it.price)}</span>
      : <span className={`${classes.price} ${classes.priceMuted}`}>ללא תוספת</span>;
  }
  return (
    <div className={classes.item}>
      {dragHandle}
      <span className={isVar ? `${classes.dot} ${it.isDefault ? classes.dotOn : ''}` : `${classes.sq} ${it.isDefault ? classes.sqOn : ''}`} />
      <span className={classes.iName}>{it.name || <span className={classes.iNameEmpty}>(ללא שם)</span>}</span>
      {isVar && it.isDefault ? <span className={classes.star}>ברירת מחדל</span> : priceEl}
    </div>
  );
};

// Architectural summary: Sortable dnd node wrapping a read-only item for מיון (reorder) mode.
const SortableReadItem: React.FC<{ g: UiGroup; it: UiItem }> = ({ g, it }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <ReadItem
        g={g}
        it={it}
        dragHandle={
          <DragHandle
            dataTestId={`options-builder-item-drag-${it.id}`}
            attributes={attributes}
            listeners={listeners}
            setActivatorNodeRef={setActivatorNodeRef}
          />
        }
      />
    </div>
  );
};

const EditItem: React.FC<{ g: UiGroup; it: UiItem; depth: number; api: GroupApi; dragHandle?: React.ReactNode }> = ({ g, it, depth, api, dragHandle }) => {
  const isVar = g.type === 'variant';
  const { max } = pickLimits(g);
  const freeMode = g.freeOn && max > 1;
  const setPriceField = (field: 'price' | 'priceWithin', value: string | number) => {
    // Mantine emits a string mid-edit (e.g. cleared field); treat unparseable as 0 instead of blocking input.
    const n = typeof value === 'number' ? value : parseFloat(value);
    api.setDraft((gs) => mapItem(gs, it.id, { [field]: Number.isFinite(n) ? normalizeTenths(n) : 0 }));
  };
  return (
    <>
      <div className={classes.itemEdit}>
        {dragHandle}
        <TextInput
          className={classes.nameInput}
          data-testid={`options-builder-item-name-${it.id}`}
          style={{ flex: 1 }}
          placeholder="שם הפריט"
          value={it.name}
          onChange={(e) => {
            const value = e.currentTarget.value;
            api.setDraft((gs) => mapItem(gs, it.id, { name: value }));
          }}
        />
        {freeMode ? (
          <span className={classes.priceField}>
            <span className={classes.tierLabel}>{firstNPhrase(g.freeLimit)}:</span>
            <PriceInput width={64} value={it.priceWithin} onChange={(v) => setPriceField('priceWithin', v)} dataTestId={`options-builder-item-price-within-${it.id}`} />
            <span className={classes.tierLabel}>· אחר כך:</span>
            <PriceInput width={64} value={it.price} onChange={(v) => setPriceField('price', v)} dataTestId={`options-builder-item-price-after-${it.id}`} />
            <span>{ILS}</span>
          </span>
        ) : (
          <span className={classes.priceField}>
            <span>+{ILS}</span>
            <PriceInput width={88} value={it.price} onChange={(v) => setPriceField('price', v)} dataTestId={`options-builder-item-price-${it.id}`} />
          </span>
        )}
        <UnstyledButton className={classes.delItem} data-testid={`options-builder-item-delete-${it.id}`} aria-label="מחק פריט" onClick={() => api.setDraft((gs) => removeItemNode(gs, it.id))}>🗑</UnstyledButton>
      </div>
      {!isVar && (
        <div className={classes.itemKds}>
          <Switch
            data-testid={`options-builder-item-kds-${it.id}`}
            checked={it.kds ?? false}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              api.setDraft((gs) => mapItem(gs, it.id, { kds: checked }));
            }}
            label="מוצג במטבח (KDS)"
          />
          <Checkbox
            className={classes.defaultToggle}
            data-testid={`options-builder-item-default-${it.id}`}
            checked={it.isDefault}
            label="ברירת מחדל"
            onChange={() => api.setDraft((gs) => toggleDefaultIn(gs, it.id))}
          />
          <MultiSelect
            className={classes.itemKdsPrinters}
            data-testid={`options-builder-item-printers-${it.id}`}
            data={api.printerOptions}
            value={it.printerIds.map(String)}
            onChange={(vals) => api.setDraft((gs) => mapItem(gs, it.id, { printerIds: vals.map(Number) }))}
            placeholder="מדפסות"
            searchable
            clearable
          />
        </div>
      )}
      {isVar && (
        <div className={classes.itemKds}>
          <Checkbox
            className={classes.defaultToggle}
            data-testid={`options-builder-item-default-${it.id}`}
            checked={it.isDefault}
            label="ברירת מחדל"
            onChange={() => api.setDraft((gs) => toggleDefaultIn(gs, it.id))}
          />
        </div>
      )}
      {max !== 1 && (
        <div className={classes.itemKds}>
          <Checkbox
            className={classes.defaultToggle}
            data-testid={`options-builder-item-limit-${it.id}`}
            checked={it.maxPick !== null}
            label="הגבל בחירה ל-"
            onChange={(e) => api.setDraft((gs) => mapItem(gs, it.id, { maxPick: e.currentTarget.checked ? (it.maxPick ?? 1) : null }))}
          />
          {it.maxPick !== null ? (
            <Stepper value={it.maxPick} min={1} max={max} set={(v) => api.setDraft((gs) => mapItem(gs, it.id, { maxPick: v }))} dataTestId={`options-item-maxpick-${it.id}`} />
          ) : null}
        </div>
      )}
      {depth < 2 && (
        <UnstyledButton className={classes.nestBtn} data-testid={`options-builder-item-add-child-${it.id}`} onClick={() => api.addChild(it.id)}>
          ＋ הוסף בחירה תחת "{it.name || 'פריט'}"
        </UnstyledButton>
      )}
      {it.childGroups.length > 0 && (
        <div className={classes.subwrap}>
          <SortableGroupList groups={it.childGroups} depth={depth + 1} api={api} parentItemId={it.id} sortable />
        </div>
      )}
    </>
  );
};

// Architectural summary: Wraps an EditItem in a dnd-kit sortable node with a drag handle.
const SortableEditItem: React.FC<{ g: UiGroup; it: UiItem; depth: number; api: GroupApi }> = ({ g, it, depth, api }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <EditItem
        g={g}
        it={it}
        depth={depth}
        api={api}
        dragHandle={
          <DragHandle
            dataTestId={`options-builder-item-drag-${it.id}`}
            attributes={attributes}
            listeners={listeners}
            setActivatorNodeRef={setActivatorNodeRef}
          />
        }
      />
    </div>
  );
};

const Example: React.FC<{ g: UiGroup }> = ({ g }) => {
  const { max } = pickLimits(g);
  const rep = Math.max(0, ...g.items.map((it) => it.price || 0)) || 6;
  const picks = Math.min(max, g.freeLimit + 1);
  const frees = Math.min(g.freeLimit, picks);
  const paid = Math.max(0, picks - frees);
  const terms = [...Array(frees).fill(0), ...Array(paid).fill(rep)] as number[];
  if (terms.length === 0) return null;
  const sum = terms.reduce((a, b) => a + b, 0);
  return (
    <div className={classes.example}>
      <span className={classes.exampleLab}>דוגמה:</span>
      <span>{picks} פריטים = {terms.map((t) => (t === 0 ? '0' : fmt(t))).join(' + ')} = <b>{fmt(sum)}</b></span>
    </div>
  );
};

const MODE_OPTIONS: { value: ComboMode; label: string }[] = [
  { value: 'upTo', label: 'עד' },
  { value: 'exactly', label: 'בדיוק' },
  { value: 'range', label: 'לפחות … ועד' },
  { value: 'unlimited', label: 'ללא הגבלה' },
];

// Architectural summary: Sibling group list; sortable mounts dnd wiring, else a plain list.
const SortableGroupList: React.FC<{ groups: UiGroup[]; depth: number; api: GroupApi; parentItemId: string | null; sortable: boolean }> = ({
  groups,
  depth,
  api,
  parentItemId,
  sortable,
}) => {
  const groupSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    api.setDraft((gs) => reorderGroupsInParent(gs, parentItemId, String(active.id), String(over.id)));
  };

  if (!sortable) {
    return <>{groups.map((group) => <GroupCard key={group.id} g={group} depth={depth} api={api} />)}</>;
  }

  return (
    <DndContext sensors={groupSensors} collisionDetection={closestCenter} onDragEnd={onGroupDragEnd}>
      <SortableContext items={groups.map((group) => group.id)} strategy={verticalListSortingStrategy}>
        {groups.map((group) => <SortableGroupCard key={group.id} g={group} depth={depth} api={api} />)}
      </SortableContext>
    </DndContext>
  );
};

const SortableGroupCard: React.FC<{ g: UiGroup; depth: number; api: GroupApi }> = ({ g, depth, api }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: g.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <GroupCard
        g={g}
        depth={depth}
        api={api}
        dragHandle={
          <DragHandle
            dataTestId={`options-builder-group-drag-${g.id}`}
            attributes={attributes}
            listeners={listeners}
            setActivatorNodeRef={setActivatorNodeRef}
          />
        }
      />
    </div>
  );
};

const GroupCard: React.FC<{ g: UiGroup; depth: number; api: GroupApi; dragHandle?: React.ReactNode }> = ({ g, depth, api, dragHandle }) => {
  const isEditing = api.editing.has(g.id);
  const errs = api.showErrors ? groupErrors(g) : {};
  const hasErr = Object.keys(errs).length > 0;
  const { max } = pickLimits(g);
  const rule = genRule(g);
  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    api.setDraft((gs) => reorderItemsInGroup(gs, g.id, String(active.id), String(over.id)));
  };

  if (api.reorderMode) {
    return (
      <div className={classes.group}>
        <div className={classes.gTop}>
          {dragHandle}
          <span className={classes.gName}>
            {g.name || <span className={classes.gNamePlaceholder}>קבוצה חדשה</span>}
            {g.type === 'variant' && g.required ? <span className={classes.req}>*</span> : null}
          </span>
          <span className={`${classes.chip} ${g.type === 'variant' ? classes.chipVar : classes.chipCombo}`}>
            {g.type === 'variant' ? 'וריאציה' : 'קומבו'}
          </span>
        </div>
        {g.items.length > 0 && (
          <DndContext sensors={itemSensors} collisionDetection={closestCenter} onDragEnd={onItemDragEnd}>
            <SortableContext items={g.items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
              <div className={classes.items}>
                {g.items.map((it) => <SortableReadItem key={it.id} g={g} it={it} />)}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    );
  }

  return (
    <div className={`${classes.group} ${isEditing ? classes.groupEditing : ''} ${hasErr ? classes.groupInvalid : ''}`}>
      <div className={classes.gTop}>
        {dragHandle}
        <span className={classes.gName}>
          {g.name || <span className={classes.gNamePlaceholder}>קבוצה חדשה</span>}
          {g.type === 'variant' && g.required ? <span className={classes.req}>*</span> : null}
        </span>
        <span className={`${classes.chip} ${g.type === 'variant' ? classes.chipVar : classes.chipCombo}`}>
          {g.type === 'variant' ? 'וריאציה' : 'קומבו'}
        </span>
        <div className={classes.gActions}>
          {!isEditing && (
            <UnstyledButton className={classes.ico} data-testid={`options-builder-group-edit-${g.id}`} aria-label="ערוך" onClick={() => api.toggleEdit(g.id, true)}>
              <FontAwesomeIcon icon={faPen as IconProp} />
            </UnstyledButton>
          )}
          <UnstyledButton className={classes.ico} data-testid={`options-builder-group-copy-${g.id}`} aria-label="העתק קבוצה ליעדים אחרים" onClick={() => api.onStartCopy(g)}>
            <FontAwesomeIcon icon={faCopy as IconProp} />
          </UnstyledButton>
          <UnstyledButton className={`${classes.ico} ${classes.icoDel}`} data-testid={`options-builder-group-delete-${g.id}`} aria-label="מחק קבוצה" onClick={() => api.delGroup(g)}>
            <FontAwesomeIcon icon={faTrashCan as IconProp} />
          </UnstyledButton>
        </div>
      </div>

      {!isEditing && (
        <>
          <div className={classes.rule}>
            {rule.lead}<b>{rule.strong}</b>{rule.tail}
            {rule.free ? <> · <span className={classes.ruleFree}>{rule.free}</span></> : null}
          </div>
          <div className={classes.items}>
            {g.items.map((it) => <ReadItem key={it.id} g={g} it={it} />)}
          </div>
          <UnstyledButton className={classes.addItem} data-testid={`options-builder-group-add-item-${g.id}`} onClick={() => { api.toggleEdit(g.id, true); api.addItem(g.id); }}>＋ הוסף פריט</UnstyledButton>
        </>
      )}

      {isEditing && (
        <div className={classes.edit}>
          <div className={classes.field}>
            <TextInput
              className={classes.nameInput}
              data-testid={`options-builder-group-name-${g.id}`}
              value={g.name}
              error={errs.name}
              placeholder="שם הקבוצה (לדוגמה: גודל, תוספות)"
              onChange={(e) => {
                const value = e.currentTarget.value;
                api.setDraft((gs) => mapGroup(gs, g.id, { name: value }));
              }}
            />
          </div>

          <div className={classes.field}>
            <span className={classes.lbl}>סוג</span>
            <div className={classes.seg}>
              <UnstyledButton
                className={`${classes.segBtn} ${g.type === 'variant' ? classes.segSel : ''}`}
                data-testid={`options-builder-group-type-variant-${g.id}`}
                onClick={() => api.setDraft((gs) => setGroupType(gs, g.id, 'variant'))}
              >
                וריאציה
              </UnstyledButton>
              <UnstyledButton
                className={`${classes.segBtn} ${g.type === 'combo' ? `${classes.segSel} ${classes.segSelCombo}` : ''}`}
                data-testid={`options-builder-group-type-combo-${g.id}`}
                onClick={() => api.setDraft((gs) => setGroupType(gs, g.id, 'combo'))}
              >
                קומבו
              </UnstyledButton>
            </div>
          </div>


          <div className={classes.field}>
            <span className={classes.lbl}>כמה הלקוח בוחר?</span>
            <div className={classes.sentence}>
              <span>בוחר</span>
              <Select
                w={140}
                data-testid={`options-builder-group-mode-${g.id}`}
                data={MODE_OPTIONS}
                value={g.mode}
                allowDeselect={false}
                onChange={(v) => api.setDraft((gs) => setGroupMode(gs, g.id, (v as ComboMode) ?? 'upTo'))}
              />
              {g.mode === 'range' && (
                <>
                  <Stepper value={g.nMin} min={1} max={g.nMax} set={(v) => api.setDraft((gs) => mapGroup(gs, g.id, { nMin: v }))} dataTestId={`options-group-nmin-${g.id}`} />
                  <span>ועד</span>
                </>
              )}

              {g.mode !== 'unlimited' && (
                <>
                  <Stepper value={g.nMax} min={g.mode === 'range' ? g.nMin : 1} max={50} set={(v) => api.setDraft((gs) => setGroupNMax(gs, g.id, v))} dataTestId={`options-group-nmax-${g.id}`} />
                  <span>פריטים</span>
                </>
              )}
            </div>
            {errs.rule && <div className={classes.errLine}>{errs.rule}</div>}
          </div>

          {max > 1 && (
            <div className={classes.freeBlk}>
              <Switch
                data-testid={`options-builder-group-free-${g.id}`}
                checked={g.freeOn}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  api.setDraft((gs) => mapGroup(gs, g.id, { freeOn: checked }));
                }}
                label={quotaNote(g)}
              />
              {g.freeOn && (
                <div className={classes.freeCount}>
                  <span>כמות חינם:</span>
                  <Stepper value={g.freeLimit} min={1} max={Math.max(1, max - 1)} set={(v) => api.setDraft((gs) => mapGroup(gs, g.id, { freeLimit: v }))} dataTestId={`options-group-freelimit-${g.id}`} />
                </div>
              )}
              {errs.free && <div className={classes.errLine}>{errs.free}</div>}
            </div>
          )}

          <DndContext sensors={itemSensors} collisionDetection={closestCenter} onDragEnd={onItemDragEnd}>
            <SortableContext items={g.items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
              <div className={classes.items} style={{ marginTop: 0 }}>
                {g.items.map((it) => <SortableEditItem key={it.id} g={g} it={it} depth={depth} api={api} />)}
              </div>
            </SortableContext>
          </DndContext>
          {errs.items && <div className={classes.errLine}>{errs.items}</div>}
          {errs.defaults && <div className={classes.errLine}>{errs.defaults}</div>}
          <UnstyledButton className={classes.addItem} data-testid={`options-builder-group-add-item-edit-${g.id}`} onClick={() => api.addItem(g.id)}>＋ הוסף פריט</UnstyledButton>

          {g.freeOn && max > 1 && <Example g={g} />}

          <UnstyledButton className={classes.doneBtn} data-testid={`options-builder-group-done-${g.id}`} onClick={() => api.toggleEdit(g.id, false)}>✓ סיום עריכה</UnstyledButton>
        </div>
      )}
    </div>
  );
};

export const OptionsBuilderDrawer = React.memo(OptionsBuilderDrawerComponent);
